import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Contact, Fingerprint, Zap, Droplets, Flame, Wifi, Phone, Tv, Fuel, Shield, Landmark, Eye, EyeOff } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useMeta } from "@/pages/Services";
import { Screen, rs } from "./ui";
import { Label, Field, Picker, Chips, Segment, Radio, FeeBox, CTA, usePay } from "./kit";

const num = (v) => Number(v || 0);

export function MRecharge() {
  const meta = useMeta();
  const [type, setType] = useState("prepaid");
  const [f, setF] = useState({ number: "", operator: "", circle: "", amount: "" });
  const [plans, setPlans] = useState([]);
  const [all, setAll] = useState(false);
  const p = usePay("/recharge");
  useEffect(() => {
    if (f.number.length === 10) api.get(`/operator-lookup/${f.number}`).then(({ data }) => setF((x) => ({ ...x, operator: x.operator || data.operator, circle: data.circle }))).catch(() => {});
  }, [f.number]);
  useEffect(() => { api.get("/plans", { params: { kind: "mobile", operator: f.operator } }).then(({ data }) => setPlans(data.plans)); }, [f.operator]);
  const go = (e) => { e.preventDefault(); p.open({ title: `${f.operator} · ${f.number}`, amount: num(f.amount), body: { kind: "mobile", ...f, amount: num(f.amount) } }); };
  return (
    <Screen title="Mobile Recharge" testid="m-recharge">
      <form onSubmit={go}>
        <Segment items={[["prepaid", "Prepaid"], ["postpaid", "Postpaid"]]} value={type} onChange={setType} testid="m-rc-type" />
        <Label>Mobile Number</Label>
        <Field inputMode="numeric" maxLength={10} value={f.number} onChange={(e) => setF({ ...f, number: e.target.value.replace(/\D/g, "") })} required right={<Contact className="h-5 w-5 text-slate-400" />} data-testid="m-rc-number" />
        <Label>Select Operator</Label>
        <Picker value={f.operator} onChange={(e) => setF({ ...f, operator: e.target.value })} required options={[{ value: "", label: "Select operator" }, ...(meta?.operators?.mobile || [])]} data-testid="m-rc-operator" />
        {type === "prepaid" && plans.length > 0 && (
          <>
            <Label right={<button type="button" onClick={() => setAll(!all)} className="text-sm font-semibold text-[#F26B1D]" data-testid="m-rc-view-all">{all ? "Show less" : "View All"}</button>}>Select a Plan</Label>
            <div className="grid grid-cols-3 gap-2" data-testid="m-rc-plans">
              {(all ? plans : plans.slice(0, 3)).map((pl) => (
                <button type="button" key={pl.amount} onClick={() => setF({ ...f, amount: String(pl.amount) })} className={`relative rounded-xl border p-3 text-left ${num(f.amount) === pl.amount ? "border-[#F26B1D] bg-orange-50/40" : "border-slate-200"}`} data-testid={`m-plan-${pl.amount}`}>
                  <div className="absolute right-2 top-2"><Radio on={num(f.amount) === pl.amount} /></div>
                  <div className="font-display text-lg font-bold">₹{pl.amount}</div>
                  <div className="mt-1 line-clamp-2 text-[11px] text-slate-500">{pl.desc.split(",")[0]}</div>
                  <div className="text-[11px] text-slate-500">{pl.validity}</div>
                </button>
              ))}
            </div>
          </>
        )}
        <Label>Amount</Label>
        <Field prefix="₹" type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} required data-testid="m-rc-amount" />
        <CTA type="submit" data-testid="m-rc-submit">Recharge Now</CTA>
      </form>
      {p.ui}
    </Screen>
  );
}

export function MDth() {
  const meta = useMeta();
  const [f, setF] = useState({ operator: "", number: "", amount: "" });
  const p = usePay("/recharge");
  const go = (e) => { e.preventDefault(); if (!f.operator) return toast.error("Select DTH provider"); p.open({ title: `${f.operator} · ${f.number}`, amount: num(f.amount), body: { kind: "dth", ...f, amount: num(f.amount) } }); };
  return (
    <Screen title="DTH Recharge" testid="m-dth">
      <form onSubmit={go}>
        <Label>Select DTH Provider</Label>
        <div className="grid grid-cols-3 gap-2" data-testid="m-dth-providers">
          {(meta?.operators?.dth || []).map((o) => (
            <button type="button" key={o} onClick={() => setF({ ...f, operator: o })} className={`flex h-24 flex-col items-center justify-center gap-2 rounded-xl border px-2 ${f.operator === o ? "border-[#F26B1D]" : "border-slate-200"}`} data-testid={`m-dth-${o.replace(/\s+/g, "-")}`}>
              <Tv className="h-6 w-6 text-[#0F2C47]" /><span className="text-center text-xs font-bold leading-tight">{o}</span><Radio on={f.operator === o} />
            </button>
          ))}
        </div>
        <Label>Subscriber ID</Label>
        <Field value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} required data-testid="m-dth-id" />
        <Label>Amount</Label>
        <Field prefix="₹" type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} required data-testid="m-dth-amount" />
        <Chips values={[500, 1000, 2000, 5000]} value={f.amount} onPick={(v) => setF({ ...f, amount: v })} testid="m-dth-chips" />
        <CTA type="submit" data-testid="m-dth-submit">Continue</CTA>
      </form>
      {p.ui}
    </Screen>
  );
}

const CAT_ICON = { Electricity: Zap, Water: Droplets, "Gas (Piped)": Flame, "LPG Booking": Fuel, Broadband: Wifi, Landline: Phone, Insurance: Shield };

export function MBillPay() {
  const meta = useMeta();
  const cats = Object.keys(meta?.bbps || {});
  const [cat, setCat] = useState("");
  const [f, setF] = useState({ biller: "", consumer_no: "" });
  const [bill, setBill] = useState(null);
  const [busy, setBusy] = useState(false);
  const p = usePay("/bbps/pay");
  useEffect(() => { if (!cat && cats.length) setCat(cats[0]); }, [cats.length]); // eslint-disable-line
  const fetchBill = async () => {
    setBusy(true);
    try { const { data } = await api.post("/bbps/fetch", { category: cat, ...f }); setBill(data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const pay = () => p.open({ title: `${f.biller} · ${f.consumer_no}`, amount: bill.bill_amount, rows: [["Customer", bill.customer_name], ["Due date", bill.due_date]],
    body: { category: cat, ...f, amount: bill.bill_amount, customer_name: bill.customer_name } });
  return (
    <Screen title="Bill Pay" testid="m-bill">
      <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4" data-testid="m-bill-cats">
        {cats.map((c) => { const I = CAT_ICON[c] || Zap; return (
          <button key={c} onClick={() => { setCat(c); setF({ biller: "", consumer_no: "" }); setBill(null); }} className={`flex h-20 w-20 shrink-0 flex-col items-center justify-center gap-1 rounded-xl border text-[11px] font-semibold ${cat === c ? "border-[#F26B1D] bg-orange-50/50" : "border-slate-200"}`} data-testid={`m-bill-cat-${c.replace(/\W+/g, "-").replace(/-$/, "")}`}>
            <I className={`h-6 w-6 ${cat === c ? "text-[#F26B1D]" : "text-[#0F2C47]"}`} />{c.replace(" (Piped)", "")}
          </button>); })}
      </div>
      <Label>Select Provider</Label>
      <Picker value={f.biller} onChange={(e) => { setF({ ...f, biller: e.target.value }); setBill(null); }} options={[{ value: "", label: "Select provider" }, ...((meta?.bbps || {})[cat] || [])]} data-testid="m-bill-provider" />
      <Label>Consumer Number</Label>
      <Field value={f.consumer_no} onChange={(e) => { setF({ ...f, consumer_no: e.target.value }); setBill(null); }} data-testid="m-bill-consumer"
        right={<button type="button" disabled={!f.biller || !f.consumer_no || busy} onClick={fetchBill} className="text-sm font-bold text-[#F26B1D] disabled:opacity-40" data-testid="m-bill-fetch">{busy ? "…" : "Fetch Bill"}</button>} />
      {bill && (
        <div className="mt-4 flex items-center justify-between rounded-xl bg-slate-100 p-4" data-testid="m-bill-card">
          <div><div className="text-xs text-slate-500">{bill.customer_name}</div><div className="font-display text-2xl font-bold">{rs(bill.bill_amount)}</div></div>
          <div className="text-right text-xs text-slate-500">Due Date<div className="text-sm font-semibold text-[#0F2C47]">{bill.due_date}</div></div>
        </div>
      )}
      <CTA disabled={!bill} onClick={pay} data-testid="m-bill-pay">Pay Bill</CTA>
      {p.ui}
    </Screen>
  );
}

export function MCreditCard() {
  const meta = useMeta();
  const [f, setF] = useState({ bank: "", card_number: "", card_holder: "", mobile: "", amount: "" });
  const p = usePay("/credit-card");
  const amt = num(f.amount);
  const fee = Math.round(amt * 0.5) / 100;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const go = (e) => { e.preventDefault(); p.open({ title: `${f.bank} card`, amount: amt + fee, rows: [["Bill amount", rs(amt)], ["Convenience fee", rs(fee)]], body: { ...f, amount: amt } }); };
  const digits = f.card_number.replace(/\D/g, "");
  return (
    <Screen title="Credit Card Bill" testid="m-cc">
      <div className="rounded-2xl bg-[#0F2C47] p-5 text-white" data-testid="m-cc-card">
        <div className="flex justify-between text-sm font-semibold"><span>{f.bank || "Your bank"} Credit Card</span><span className="flex"><span className="h-6 w-6 rounded-full bg-red-500" /><span className="-ml-2 h-6 w-6 rounded-full bg-amber-400/90" /></span></div>
        <div className="mt-6 font-mono text-lg tracking-[0.2em]">XXXX XXXX XXXX {digits.slice(-4).padStart(4, "•")}</div>
        <div className="mt-2 text-xs uppercase tracking-wider text-white/70">{f.card_holder || "Card holder"}</div>
      </div>
      <form onSubmit={go}>
        <Label>Bank</Label><Picker value={f.bank} onChange={set("bank")} required options={[{ value: "", label: "Select bank" }, ...(meta?.banks || [])]} data-testid="m-cc-bank" />
        <Label>Card Number</Label><Field inputMode="numeric" maxLength={19} value={f.card_number} onChange={set("card_number")} required className="font-mono" data-testid="m-cc-number" />
        <Label>Card Holder Name</Label><Field value={f.card_holder} onChange={set("card_holder")} required data-testid="m-cc-holder" />
        <Label>Mobile</Label><Field inputMode="numeric" maxLength={10} value={f.mobile} onChange={set("mobile")} required data-testid="m-cc-mobile" />
        <Label>Enter Amount</Label><Field prefix="₹" type="number" value={f.amount} onChange={set("amount")} required data-testid="m-cc-amount" />
        <Chips values={[1000, 2000, 5000, 10000]} value={f.amount} onPick={(v) => setF({ ...f, amount: v })} testid="m-cc-chips" />
        {amt > 0 && <FeeBox fee={fee} total={amt + fee} feeLabel="Convenience Fee" />}
        <CTA type="submit" data-testid="m-cc-submit">Pay Bill</CTA>
      </form>
      {p.ui}
    </Screen>
  );
}

export function MCashDeposit() {
  const meta = useMeta();
  const [f, setF] = useState({ bank: "", account_no: "", customer_name: "", mobile: "", amount: "" });
  const p = usePay("/cash-deposit");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const amt = num(f.amount);
  const go = (e) => { e.preventDefault(); p.open({ title: `${f.bank} · ${f.account_no}`, amount: amt, rows: [["Depositor", f.customer_name], ["Mobile", f.mobile]], body: { ...f, amount: amt } }); };
  return (
    <Screen title="Cash Deposit" testid="m-cash">
      <form onSubmit={go}>
        <Label>Select Bank</Label><Picker value={f.bank} onChange={set("bank")} required options={[{ value: "", label: "Select bank" }, ...(meta?.banks || [])]} data-testid="m-cd-bank" />
        <Label>Account Number</Label><Field inputMode="numeric" value={f.account_no} onChange={set("account_no")} required className="font-mono" right={<Landmark className="h-5 w-5 text-slate-400" />} data-testid="m-cd-account" />
        <Label>Depositor Name</Label><Field value={f.customer_name} onChange={set("customer_name")} required data-testid="m-cd-name" />
        <Label>Depositor Mobile Number</Label><Field inputMode="numeric" maxLength={10} value={f.mobile} onChange={set("mobile")} required data-testid="m-cd-mobile" />
        <Label>Amount to Deposit</Label><Field prefix="₹" type="number" value={f.amount} onChange={set("amount")} required data-testid="m-cd-amount" />
        <Chips values={[1000, 2000, 5000, 10000]} value={f.amount} onPick={(v) => setF({ ...f, amount: v })} testid="m-cd-chips" />
        {amt > 0 && <FeeBox fee={null} total={amt} />}
        <CTA type="submit" data-testid="m-cd-submit">Continue</CTA>
      </form>
      {p.ui}
    </Screen>
  );
}

export function MAeps({ fallback, aadhaarPay }) {
  const meta = useMeta();
  const [st, setSt] = useState(null);
  const [type, setType] = useState(aadhaarPay ? "AP" : "CW");
  const [f, setF] = useState({ bank: "", aadhaar: "", mobile: "", amount: "" });
  const [show, setShow] = useState(false);
  const [ok, setOk] = useState(false);
  const p = usePay("/aeps/transaction");
  useEffect(() => { api.get("/aeps/status").then(({ data }) => setSt(data)).catch(() => setSt({})); }, []);
  if (!st) return <Screen title="AEPS"><div className="py-10 text-center text-sm text-slate-400">Loading…</div></Screen>;
  if (!st.onboarded || !st.twofa_today) return <Screen title={aadhaarPay ? "Aadhaar Pay" : "AEPS"}><div className="m-embed">{fallback}</div></Screen>;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const needAmt = type === "CW" || type === "AP";
  const go = (e) => { e.preventDefault(); if (!ok) return toast.error("Please give Aadhaar consent"); p.open({ title: `${f.bank} · XXXX${f.aadhaar.slice(-4)}`, amount: needAmt ? num(f.amount) : 0, body: { txn_type: type, ...f, amount: needAmt ? num(f.amount) : 0 } }); };
  return (
    <Screen title={aadhaarPay ? "Aadhaar Pay" : "AEPS"} testid="m-aeps">
      <form onSubmit={go}>
        {!aadhaarPay && <Segment items={[["CW", "Cash Withdrawal"], ["BE", "Balance Enquiry"], ["MS", "Mini Statement"]]} value={type} onChange={setType} testid="m-aeps-type" />}
        <Label>Select Bank</Label><Picker value={f.bank} onChange={set("bank")} required options={[{ value: "", label: "Select bank" }, ...(meta?.banks || [])]} data-testid="m-aeps-bank" />
        <Label>Aadhaar Number</Label>
        <Field type={show ? "text" : "password"} inputMode="numeric" maxLength={12} value={f.aadhaar} onChange={(e) => setF({ ...f, aadhaar: e.target.value.replace(/\D/g, "") })} required className="font-mono"
          right={<button type="button" onClick={() => setShow(!show)} data-testid="m-aeps-eye">{show ? <EyeOff className="h-5 w-5 text-slate-400" /> : <Eye className="h-5 w-5 text-slate-400" />}</button>} data-testid="m-aeps-aadhaar" />
        <Label>Customer Mobile Number</Label><Field inputMode="numeric" maxLength={10} value={f.mobile} onChange={set("mobile")} required data-testid="m-aeps-mobile" />
        {needAmt && <><Label>Amount</Label><Field prefix="₹" type="number" value={f.amount} onChange={set("amount")} required data-testid="m-aeps-amount" />
          <Chips values={[500, 1000, 2000, 5000]} value={f.amount} onPick={(v) => setF({ ...f, amount: v })} testid="m-aeps-chips" /></>}
        <div className="mt-5 flex items-center gap-3 rounded-xl bg-emerald-50 p-4" data-testid="m-aeps-device">
          <span className="grid h-12 w-12 place-items-center rounded-full bg-emerald-100 text-emerald-700"><Fingerprint className="h-7 w-7" /></span>
          <div className="flex-1"><div className="text-sm font-bold">Biometric device</div><div className="text-xs text-slate-500">Connect your RD-service device (Mantra / Morpho) by OTG</div></div>
        </div>
        <label className="mt-4 flex items-start gap-2 text-xs text-slate-600"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#F26B1D]" data-testid="m-aeps-consent" />I hereby consent to use my Aadhaar for AEPS transaction.</label>
        <CTA type="submit" data-testid="m-aeps-submit">Authenticate</CTA>
      </form>
      {p.ui}
    </Screen>
  );
}
